import React, { useEffect, useState } from "react"
import { ItemPicker } from "@tenforce/product-fields"
import { IItem } from "@tenforce/product-fields/itemPicker/types"

import { getItemAncestry } from "../../../../../apis/selectors"
import { IItemPickerField } from "../../.."
import { useAppDispatch, useAppSelector } from "../../../../../hooks"
import { GlobalState } from "../../../../../store"
import HTMLAndInternalLinkParser from "../../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../../util/Translations"
import { addErrorNotification } from "../../../../notifications/actions"
import { getItemDetailUrl } from "../../../../../util/UrlGenerator"
import env from "../../../../../env"
import { fetchVisibleFields } from "../../../../collection/slice"

type Props = IItemPickerField

const ItemPickerNew: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [itemsWithParents, setItemsWithParents] = useState<IItem[]>([])
    const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([])
    const {
        description,
        isSingleSelect,
        value: selectedItems,
        isDisabled,
        itemId,
        fieldId,
        odataType,
    } = props

    const { isLoading: globalIsLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)

    const { visibleFields, isQueryProcessing, visibleFieldsIds } =
        useAppSelector((state: GlobalState) => state.collection)

    const fetchAncestry = async (itemId: number): Promise<IItem[]> => {
        try {
            let parents = await getItemAncestry(itemId)
            if (parents.length > 0) {
                parents = parents.map((parent) => ({
                    ...parent,
                    url: getItemDetailUrl(env.applicationPath, "", parent.id),
                }))
            }
            return parents
        } catch (error) {
            dispatch(
                addErrorNotification(
                    error instanceof Error
                        ? error.message
                        : "Something went wrong when fetching ancestry.",
                ),
            )
            return []
        }
    }

    useEffect(() => {
        const fetchParentsForItems = async (): Promise<void> => {
            const updatedItems = []
            const selectedIds: number[] = []
            for (const item of selectedItems) {
                selectedIds.push(item.id)
                const parentsData = await fetchAncestry(item.id)
                updatedItems.push({
                    ...item,
                    url: getItemDetailUrl(env.applicationPath, "", item.id),
                    parents: parentsData,
                })
            }
            setSelectedItemsIds(selectedIds)
            setItemsWithParents(updatedItems)
        }
        fetchParentsForItems()
    }, [selectedItems])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const fetchCollectionData = (): void => {
        dispatch(fetchVisibleFields({ itemId, fieldId, odataType }))
    }

    const {
        ItemPickerAddItem,
        ItemPickerEditItem,
        ItemPickerEditItems,
        ItemPickerAddItems,
    } = Labels

    return (
        <>
            <ItemPicker
                {...props}
                isDisabled={isFieldDisabled}
                description={
                    <HTMLAndInternalLinkParser
                        content={(description as string) ?? ""}
                    />
                }
                fieldId={props.fieldId}
                title={props.title}
                isLoading={globalIsLoading}
                isSingleSelect={isSingleSelect}
                labels={{
                    addItem: ItemPickerAddItem,
                    editItem: ItemPickerEditItem,
                    editItems: ItemPickerEditItems,
                    addItems: ItemPickerAddItems,
                }}
                value={itemsWithParents}
                collection={{
                    query: {
                        visibleFields: visibleFields,
                        selectedItemIds: selectedItemsIds,
                        visibleFieldsIds: visibleFieldsIds,
                        isQueryProcessing,
                    },
                }}
                fetchCollectionData={fetchCollectionData}
            />
        </>
    )
}

export default ItemPickerNew
